import { Injectable } from "@angular/core";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { environment } from "src/environments/environment";
import { UserService } from "../authentication/user.service";

/**
 * URLs the SDK's automatic dependency tracking must never instrument.
 *
 * Nova's Realtime voice path calls OpenAI directly from the browser
 * (nova-realtime-session.service.ts POSTs the SDP offer to
 * https://api.openai.com/v1/realtime/calls) rather than through our own API, and the SDK
 * auto-instruments every outgoing fetch/XHR on the page - OpenAI's included. It adds its own
 * "Request-Id" correlation header to that call, which OpenAI's CORS preflight does not allow, so
 * the browser blocks the request outright before it is ever sent:
 *   Access to fetch at 'https://api.openai.com/v1/realtime/calls' from origin '...' has been
 *   blocked by CORS policy: Request header field request-id is not allowed by
 *   Access-Control-Allow-Headers in preflight response.
 * Excluding the URL from auto-tracking altogether - rather than only suppressing correlation
 * headers - is what actually stops the header being added: this app never needed telemetry on a
 * third-party API call anyway, so there is nothing lost by leaving it untouched.
 *
 * A plain exported constant, not inline in the config object, for two reasons. It can be asserted
 * on directly: constructing the real service needs a connection string the test environment
 * deliberately leaves empty (no telemetry from local/CI runs), which would otherwise put this
 * behind a branch a spec cannot reach. And it is shared: app.component.ts opens a SECOND,
 * independent ApplicationInsights instance (its own reasons, not this service's), which installs
 * its own hook on the same global window.fetch - excluding OpenAI here does nothing to stop that
 * other instance instrumenting the same call, so both configs use this one constant rather than
 * two copies that could drift apart.
 */
export const APP_INSIGHTS_EXCLUDED_FROM_AUTO_TRACKING: RegExp[] = [
  /^https:\/\/api\.openai\.com\//i,
];

@Injectable({
  providedIn: "root",
})
export class ApplicationInsightsService {
  appInsights: ApplicationInsights;
  connectionString: string;

  constructor(private userService: UserService) {
    const connectionString = environment.appInsights.connectionString;
    if (!connectionString) {
      // add custom behavior to handle this scenario
      console.error("Application Insights connection string is not found.");
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        enableAutoRouteTracking: true, // optional: auto-log all route changes
        excludeRequestFromAutoTrackingPatterns: APP_INSIGHTS_EXCLUDED_FROM_AUTO_TRACKING,
      },
    });
    this.appInsights.loadAppInsights();
  }

  // helper methods to track a variety of events and metric
  logPageView(name?: string, url?: string) {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.trackPageView({
      name: name,
      uri: url,
    });
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    if (!this.appInsights) {
      return;
    }

    let userProperties = this.getUserProperties();
    properties = { ...userProperties, ...properties };
    this.appInsights.trackEvent({ name: name }, properties);
  }

  logMetric(
    name: string,
    average: number,
    properties?: { [key: string]: any }
  ) {
    if (!this.appInsights) {
      return;
    }
    let userProperties = this.getUserProperties();
    properties = { ...userProperties, ...properties };
    this.appInsights.trackMetric({ name: name, average: average }, properties);
  }

  logException(
    exception: Error,
    severityLevel?: number,
    properties?: { [key: string]: any }
  ) {
    if (!this.appInsights) {
      return;
    }

    let userProperties = this.getUserProperties();
    properties = { ...userProperties, ...properties };
    this.appInsights.trackException(
      {
        exception: exception,
        severityLevel: severityLevel,
      },
      properties
    );
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    if (!this.appInsights) {
      return;
    }
    let userProperties = this.getUserProperties();
    properties = { ...userProperties, ...properties };
    this.appInsights.trackTrace({ message: message }, properties);
  }

  private getUserProperties() {
    const user = this.userService.getUser();
    if (!user) {
      return {};
    }

    return {
      UserGuid: user.userGuid,
      UserName: user.username,
    };
  }
}
